/**
 * Slice LV-02 · LeviCommandConsole — the conversational skills surface,
 * mounted inside the shared LeviAssistantDrawer.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mic, StopCircle, Loader2, Sparkles, CheckCircle2, X,
  Volume2, VolumeX, Keyboard, ArrowRight, AlertTriangle,
} from "lucide-react";
import { useLeviCommand } from "@/hooks/useLeviCommand";
import { isTtsEnabled, setTtsEnabled, stopSpeaking, primeTts, disposePersistentPlayback } from "@/lib/leviSpeech";
import { useLang } from "@/context/LangContext";
import LeviVoiceIntake from "@/components/levi/LeviVoiceIntake";
import LeviListeningVisualizer from "@/components/levi/LeviListeningVisualizer";

const LABEL = {
  en: {
    header: "How can I help?",
    tagline: "Start a new estimate, find a document, prepare a proposal, or take the next step on a job.",
    tap: "Tap to speak", stop: "Stop and send", type: "Type instead",
    tts_on: "Levi voice on", tts_off: "Levi voice off",
    confirm: "Yes, send it", nope: "No, cancel", open: "Open",
    speaking: "Speaking…", listening: "Listening…", thinking: "Thinking…",
    uploading: "Sending…", sent: "Done. It's on its way.",
    awaiting: "Awaiting your confirmation.", idle: "Ready.",
    try_saying: "Try saying",
    example_1: "Start a new estimate",
    example_2: "Send me the Brooks estimate",
    example_3: "Find a contract",
    example_4: "What needs my attention today?",
    conv_active: "Conversation active",
    conv_pause: "Pause conversation",
    conv_end: "End conversation",
    conv_inactivity: "Conversation paused for inactivity — tap the mic to continue",
    conv_auto_listen: "Waiting for your reply…",
    presence_listening: "Listening",
    presence_thinking: "Thinking",
    presence_speaking: "Speaking",
    presence_standby: "Standby",
    presence_ended: "Ended",
  },
  es: {
    header: "¿En qué te ayudo?",
    tagline: "Inicia un estimado, busca un documento, prepara una propuesta o continúa con el siguiente paso de un trabajo.",
    tap: "Toca para hablar", stop: "Detener y enviar", type: "Escribir en su lugar",
    tts_on: "Voz de Levi activada", tts_off: "Voz de Levi apagada",
    confirm: "Sí, envíalo", nope: "No, cancelar", open: "Abrir",
    speaking: "Hablando…", listening: "Escuchando…", thinking: "Pensando…",
    uploading: "Enviando…", sent: "Listo. Fue enviado.",
    awaiting: "Esperando tu confirmación.", idle: "Listo.",
    try_saying: "Prueba a decir",
    example_1: "Inicia un estimado",
    example_2: "Envíame el estimado de Brooks",
    example_3: "Busca un contrato",
    example_4: "¿Qué necesita mi atención hoy?",
    conv_active: "Conversación activa",
    conv_pause: "Pausar conversación",
    conv_end: "Terminar conversación",
    conv_inactivity: "Conversación pausada por inactividad — toca el micrófono para continuar",
    conv_auto_listen: "Esperando tu respuesta…",
    presence_listening: "Escuchando",
    presence_thinking: "Pensando",
    presence_speaking: "Hablando",
    presence_standby: "En espera",
    presence_ended: "Terminado",
  },
};

export default function LeviCommandConsole({ onClose, startInTypeMode = false, mode = "command", onSwitchMode }) {
  const { lang } = useLang();
  const t = LABEL[lang === "es" ? "es" : "en"];
  const navigate = useNavigate();
  const cmd = useLeviCommand();
  const [textOnly, setTextOnly] = useState(!isTtsEnabled());
  // LEVI-FIRST-01 v2 · When the console is opened from the Hero's
  // "Type to Levi" affordance, `startInTypeMode` is true so we render
  // the textarea as the primary CTA from the very first paint (no
  // voice-first Tap-to-speak flash) and the cursor lands in the
  // textarea automatically. When opened from any other entry point
  // (shell "Levi" button, floating launcher, hero "Talk to Levi"),
  // the console keeps its original voice-first behavior.
  const [typeMode, setTypeMode] = useState(!!startInTypeMode);
  const [draft, setDraft] = useState("");
  const textareaRef = useRef(null);
  const bridgedRef = useRef(null);
  // LV-CONV-01 · Conversation-first upgrade. `transcriptRef` scrolls
  // the transcript viewport to its bottom whenever new turns arrive
  // so the contractor keeps seeing the latest exchange without
  // sacrificing scrollback. Purely presentational — does not touch
  // cmd.turns, session persistence, or command architecture.
  const transcriptRef = useRef(null);
  useEffect(() => {
    const el = transcriptRef.current;
    if (!el) return;
    // Only auto-scroll when the contractor is already near the bottom
    // so scrollback review of earlier turns is not interrupted.
    const nearBottom = el.scrollHeight - el.clientHeight - el.scrollTop < 120;
    if (nearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [cmd.turns?.length, cmd.lastVisible]);

  // LEVI-BEH-01 correction 3 · When mode === "voice_intake" the
  // existing intake surface is mounted INLINE below the unified
  // header + transcript. The command console remains visible;
  // returning to command-mode only requires the intake's completion
  // or cancel callback to fire (see onIntakeCompleted /
  // onIntakeCancelled below). No whole-drawer swap.
  const inIntake = mode === "voice_intake";

  // LEVI-DOC-PREP-01 · Defect B · Bounded handoff context captured
  // at the moment we switch to voice_intake. The backend
  // `_is_start_new_estimate` bridge emits a `context` block on the
  // reply carrying { project_id, customer_name, document_type }. We
  // snapshot it here so the intake surface (mounted below) receives
  // it exactly once — subsequent replies to the command session
  // must not silently reconfigure the live intake.
  const [intakeContext, setIntakeContext] = useState(null);

  useEffect(() => { if (cmd.status === "listening") stopSpeaking("status:listening"); }, [cmd.status]);

  // Autofocus the textarea when the console mounts in type-mode so
  // the contractor's cursor is ready to type immediately.
  useEffect(() => {
    if (startInTypeMode && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [startInTypeMode]);

  // Phase 7 correction (2026-08-04) · The legacy voice_intake bridge
  // has been retired. Product Direction Lock 2026-08-04 requires one
  // continuous Levi conversation surface — the backend now asks the
  // next natural business question in the same transcript rather
  // than emitting workflow=voice_intake. This effect is intentionally
  // a no-op; the LeviVoiceIntake component is retained only for its
  // remaining deep-link entry outside the drawer.

  const toggleTts = () => {
    const next = !textOnly;
    setTextOnly(next);
    setTtsEnabled(!next);
    if (!next) primeTts("toggle-on");
    if (next) {
      stopSpeaking("toggle-off");
      // PO 2026-02 · Persistent Playback Owner teardown. Contractor
      // toggling TTS OFF is a genuine playback-owner destruction
      // point (owner change).
      disposePersistentPlayback("tts_off");
    }
  };

  const nav = (route) => { if (route) { onClose?.(); navigate(route); } };

  return (
    <div className="flex flex-col h-full min-h-0 space-y-4" data-testid="levi-command-console">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-blue-100 p-2 shrink-0">
          <Sparkles size={16} className="text-blue-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wide text-[var(--jc-text-soft)]">
            Levi
          </div>
          <div className="text-base font-semibold mt-0.5" data-testid="levi-cmd-header">{t.header}</div>
          <p className="text-xs text-[var(--jc-text-soft)] mt-1" data-testid="levi-cmd-tagline">{t.tagline}</p>
          {/* Phase 7 · Package 7-A · focused-project echo. Rendered
              only when the server's Group 1 cascade resolved a
              project and the flag is on. Read-only header signal;
              never editable, never clickable, never a new entry
              point. Preserves the one-Levi-presence guarantee. */}
          {cmd.lastExtra?.focused_project?.id && (
            <div className="mt-1 text-[11px] text-[var(--jc-text-soft)]"
                 data-testid="levi-cmd-focused-project">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-800">
                {lang === "es" ? "Trabajando en" : "Working on"}
                <span className="font-medium text-blue-900">
                  {cmd.lastExtra.focused_project.customer_name || "—"}
                  {cmd.lastExtra.focused_project.job_type
                    ? ` · ${cmd.lastExtra.focused_project.job_type}`
                    : ""}
                </span>
              </span>
            </div>
          )}
        </div>
        <button type="button" onClick={toggleTts}
          className="text-xs text-[var(--jc-text-soft)] inline-flex items-center gap-1 underline"
          data-testid="levi-cmd-tts-toggle" title={textOnly ? t.tts_off : t.tts_on}>
          {textOnly ? <VolumeX size={14}/> : <Volume2 size={14}/>}
        </button>
      </div>

      {/* LEVI-UNIFIED-01 · Contractor-friendly example suggestions
          shown only when the conversation is empty and Levi is
          idle. Clicking an example places the phrase in the
          textarea (type-mode) or submits it directly (voice-mode).
          These are examples, not modes — every suggestion is
          handled by the same conversation surface behind the
          scenes. */}
      {(!cmd.turns || cmd.turns.length === 0)
        && (cmd.status === "idle" || cmd.status === "opened") && (
        <div
          data-testid="levi-cmd-suggestions"
          className="rounded-md bg-[var(--jc-bg)] p-3 space-y-2"
        >
          <div className="overline text-[var(--jc-text-soft)]">{t.try_saying}</div>
          <div className="flex flex-wrap gap-2">
            {[t.example_1, t.example_2, t.example_3, t.example_4].map((ex, i) => (
              <button
                key={i}
                type="button"
                data-testid={`levi-cmd-suggestion-${i + 1}`}
                onClick={() => {
                  if (typeMode) {
                    setDraft(ex);
                    textareaRef.current?.focus();
                  } else {
                    cmd.submitText(ex);
                  }
                }}
                className="inline-flex items-center gap-1 rounded-full border border-[var(--jc-border)] bg-white px-3 py-1 text-xs hover:border-[var(--jc-navy)] hover:bg-[var(--jc-navy)] hover:text-white transition"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Status */}
      <div className="text-xs text-[var(--jc-text-soft)] inline-flex items-center gap-2" data-testid="levi-cmd-status">
        {(cmd.status === "uploading" || cmd.status === "thinking") && <Loader2 size={12} className="animate-spin"/>}
        {cmd.status === "listening" && <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse"/>}
        {{
          idle: t.idle, listening: t.listening, uploading: t.uploading, thinking: t.thinking,
          awaiting_confirmation: t.awaiting, executing: t.thinking, sent: t.sent,
          opened: t.idle, unclear: t.idle, error: cmd.error,
        }[cmd.status] || t.idle}
      </div>

      {/* Phase 7-B · Conversation Flow controls. Visible only during
          an active conversation. The active-state chip makes the
          loop legible to the contractor; the Pause and End pills
          give unambiguous manual control at every turn.
          Phase 7-C · adds Mute / Unmute + presence badge. */}
      {cmd.conversationActive && (
        <div className="flex flex-wrap items-center gap-2 max-w-full min-w-0"
             data-testid="levi-cmd-conversation-controls">
          <span
            data-testid="levi-cmd-presence"
            data-presence={cmd.presence || "standby"}
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800 whitespace-nowrap"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {t[`presence_${cmd.presence || "standby"}`] || t.conv_active}
          </span>
          <button
            type="button"
            data-testid="levi-cmd-pause-conversation"
            onClick={() => cmd.pauseConversation && cmd.pauseConversation()}
            className="text-xs rounded-full border border-slate-200 bg-white px-2.5 py-1 hover:bg-slate-50 transition-colors"
          >
            {t.conv_pause}
          </button>
          <button
            type="button"
            data-testid="levi-cmd-end-conversation"
            onClick={() => cmd.endConversation && cmd.endConversation({ reason: "ui" })}
            className="text-xs rounded-full border border-slate-200 bg-white px-2.5 py-1 hover:bg-slate-50 transition-colors"
          >
            {t.conv_end}
          </button>
          {cmd.lastExtra && cmd.lastExtra.expects_reply
            && cmd.status !== "listening" && (
            <span
              data-testid="levi-cmd-auto-listening-badge"
              className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs text-sky-800"
            >
              {t.conv_auto_listen}
            </span>
          )}
        </div>
      )}

      {/* Phase 7-B · Inactivity notice. Shown once the 30-second
          ceiling has elapsed with no turn from either party. The
          contractor must deliberately tap the mic to resume. */}
      {cmd.inactivityNotice && (
        <div
          data-testid="levi-cmd-inactivity-notice"
          className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"
          aria-live="polite"
        >
          {t.conv_inactivity}
        </div>
      )}

      {/* Recording-in-progress live indicator + iOS keep-alive
          surface. During listening we mount LeviListeningVisualizer,
          which draws a live waveform from the mic and plays a
          canvas-captured synthetic MediaStream in a visible <video>
          element. iOS treats that as active media playback and
          suppresses Auto-Lock, matching Vision's proven behavior
          without activating the camera. Reassures the contractor and
          serves as the keep-alive mechanism. */}
      {cmd.status === "listening" && (
        <LeviListeningVisualizer
          active={true}
          getVadWaveform={cmd.getVadWaveform}
          getVadFftSize={cmd.getVadFftSize}
          lang={lang}
        />
      )}

      {/* Conversation trail — the same LEVI-FIRST-01 v2 fix.
          Renders the user's transcribed turns and Levi's replies from
          the shared useLeviCommand.turns array so the contractor
          can see what they said, what Levi heard, and what Levi
          answered. No second conversation session — cmd.turns is
          exactly the array the backend maintains on
          db.levi_command_sessions.
          LV-CONV-01 (2026-08-22) · The transcript is now the
          dominant working region of the drawer. Removed the prior
          `max-h-64` cap and switched to a viewport-relative height
          budget so long conversations remain scrollable while the
          most recent exchange stays visible. */}
      {cmd.turns && cmd.turns.length > 0 && (
        <div
          ref={transcriptRef}
          data-testid="levi-cmd-conversation"
          className="jc-levi-transcript rounded-md border border-[var(--jc-border)] bg-white p-3 space-y-3 flex-1 min-h-[220px] overflow-y-auto"
          style={{
            overscrollBehavior: "contain",
            scrollbarWidth: "thin",
            scrollbarColor: "var(--jc-border-strong) transparent",
          }}
          aria-live="polite"
        >
          {cmd.turns.map((turn, i) => {
            const isUser = turn.role === "user";
            return (
              <div
                key={i}
                data-testid={`levi-cmd-turn-${turn.role}-${i}`}
                className={
                  isUser
                    ? "flex justify-end"
                    : "flex justify-start"
                }
              >
                <div
                  className={
                    "max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm shadow-sm " +
                    (isUser
                      ? "bg-[var(--jc-navy)] text-white"
                      : "bg-[var(--jc-bg)] text-[var(--jc-text)] border border-[var(--jc-border)]")
                  }
                >
                  <div className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-1">
                    {isUser
                      ? (lang === "es" ? "Tú" : "You")
                      : "Levi"}
                  </div>
                  <div className="whitespace-pre-wrap leading-snug">{turn.text}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Phase 7 · Package 7-A · attention signals rendered as a
          typed chip below the transcript. Structured, deterministic,
          language-neutral rendering — no free text. Silence is the
          default; if `attention` is absent the block does not
          render. */}
      {Array.isArray(cmd.lastExtra?.attention) && cmd.lastExtra.attention.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs space-y-1"
             data-testid="levi-cmd-attention">
          <div className="uppercase tracking-wide text-[10px] font-semibold text-amber-800">
            {lang === "es" ? "Levi notó" : "Levi noticed"}
          </div>
          {cmd.lastExtra.attention.map((s, i) => {
            const label = (() => {
              if (s.signal_kind === "unresolved_pending_override") {
                return lang === "es"
                  ? "Un cambio quedó sin confirmar"
                  : "An override is still pending";
              }
              if (s.signal_kind === "stale_confirmed_send") {
                const days = s.age_days || 0;
                const rk = s.related_kind || "";
                if (lang === "es") {
                  return `Sin respuesta al ${rk || "envío"} hace ${days} días`;
                }
                return `No reply on the ${rk || "send"} in ${days} days`;
              }
              if (s.signal_kind === "long_since_activity") {
                const days = s.age_days || 0;
                return lang === "es"
                  ? `Sin actividad hace ${days} días`
                  : `No activity in ${days} days`;
              }
              return s.signal_kind;
            })();
            return (
              <div key={i}
                   data-testid={`levi-cmd-attention-signal-${s.signal_kind}`}
                   className="flex items-center gap-2 text-amber-900">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                <span>{label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Latest Levi utterance — kept as a fallback for the very
          first response before the turns array has synced. Hidden
          once turns render so the contractor sees the reply exactly
          once in the conversation view. */}
      {cmd.lastVisible && !(cmd.turns && cmd.turns.length > 0) && (
        <div className="rounded-md border border-[var(--jc-border)] bg-[var(--jc-surface)] p-3 text-sm" data-testid="levi-cmd-latest">
          {cmd.lastVisible}
        </div>
      )}

      {/* LEVI-BEH-01 correction 3 · Inline intake surface. Renders
          the SAME existing LeviVoiceIntake component the deterministic
          bridge used to route to via a whole-drawer swap. Nothing
          about the intake internals changes — the unified header +
          transcript above remain mounted so the contractor never
          feels they have left Levi. On completion or cancellation
          the intake calls back to the command hook to append a
          truthful unified turn (per LEVI-BEH-01 §3). */}
      {inIntake && (
        <div
          data-testid="levi-cmd-inline-intake"
          className="rounded-md border border-[var(--jc-border)] bg-white p-3"
        >
          <LeviVoiceIntake
            onClose={onClose}
            hideOwnHeader
            initialContext={intakeContext}
            onIntakeCompleted={(intakeSession) => {
              const name = intakeSession?.structured_intake?.customer?.name
                            || (lang === "es" ? "el cliente" : "the customer");
              const followup = lang === "es"
                ? `Continuemos con el estimado para ${name}.`
                : `Continue with the estimate for ${name}.`;
              setIntakeContext(null);
              onSwitchMode?.("command");
              // The follow-up rides on the unified command session and
              // yields a real Levi turn so the transcript above shows
              // the same conversation continuing — no separate flow.
              cmd.submitText?.(followup);
            }}
            onIntakeCancelled={({ hadFacts }) => {
              setIntakeContext(null);
              onSwitchMode?.("command");
              // Silent close when the contractor never volunteered any
              // fact — the transcript is not padded with noise. If
              // they DID share facts, we append a truthful unified
              // turn confirming nothing was created.
              if (hadFacts) {
                const line = lang === "es"
                  ? "Cerré el registro de cliente sin crear nada. ¿Qué sigue?"
                  : "I closed the customer intake without creating anything. What's next?";
                cmd.submitText?.(line);
              }
            }}
          />
        </div>
      )}

      {/* Multi-match disambiguation */}
      {cmd.lastMatches?.length > 1 && (
        <div className="space-y-2" data-testid="levi-cmd-matches">
          {cmd.lastMatches.map((m) => (
            <button key={m.id} type="button"
              onClick={() => {
                // LEVI-DOC-PREP-02 · PO-QA Round 2 · UI parity.
                // Clicking a disambiguation card must behave EXACTLY
                // like typing/saying the customer name — resume the
                // pending document_prep objective and navigate to the
                // correct list. We send a compact utterance (the
                // customer name only) so the shared awaiting_project_
                // choice consumer resolves cleanly, and we also pass
                // an explicit active_project_id override so the
                // consumer's deterministic candidate-id path fires
                // even if the tenant's customer_name is not unique.
                cmd.submitText(m.customer_name || "", { activeProjectId: m.id });
              }}
              className="w-full text-left rounded border border-[var(--jc-border)] p-2 text-sm hover:bg-[var(--jc-surface)]"
              data-testid={`levi-cmd-match-${m.id}`}>
              <div className="font-medium">{m.customer_name || "—"}</div>
              <div className="text-xs text-[var(--jc-text-soft)]">
                {m.job_address || "—"}{m.job_type ? ` · ${m.job_type}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Awaiting confirmation controls */}
      {!inIntake && cmd.status === "awaiting_confirmation" && (
        <div className="flex flex-col gap-2" data-testid="levi-cmd-confirm-controls">
          <button type="button" onClick={cmd.confirm}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-black text-white px-4 py-3 text-sm font-semibold"
            data-testid="levi-cmd-confirm-btn">
            <CheckCircle2 size={16}/> {t.confirm}
          </button>
          <button type="button" onClick={cmd.cancel}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md border px-4 py-3 text-sm"
            data-testid="levi-cmd-cancel-btn">
            <X size={16}/> {t.nope}
          </button>
        </div>
      )}

      {/* Idle listen button — hidden in typeMode so voice is a
          secondary option, not the primary CTA, when the contractor
          arrived via "Type to Levi". Also hidden when the inline
          intake surface is mounted so the contractor has one clear
          primary control at a time. */}
      {!inIntake && !typeMode && (cmd.status === "idle" || cmd.status === "opened" || cmd.status === "sent" || cmd.status === "unclear" || cmd.status === "error") && (
        <div className="flex flex-col gap-2">
          <button type="button" onClick={cmd.startListening}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-red-600 text-white px-4 py-3 text-base font-semibold"
            data-testid="levi-cmd-mic-btn">
            <Mic size={18}/> {t.tap}
          </button>
          {cmd.lastExtra?.navigate_to && (
            <button type="button" onClick={() => nav(cmd.lastExtra.navigate_to)}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm"
              data-testid="levi-cmd-open-btn">
              {t.open} <ArrowRight size={14}/>
            </button>
          )}
        </div>
      )}
      {!inIntake && cmd.status === "listening" && (
        <button type="button" onClick={cmd.stopAndSend}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-black text-white px-4 py-3 text-base font-semibold"
          data-testid="levi-cmd-stop-btn">
          <StopCircle size={18}/> {t.stop}
        </button>
      )}

      {/* Text fallback / primary text input. In non-typeMode this
          appears as a compact "Type instead" underline link so voice
          stays primary. In typeMode the textarea is rendered as the
          primary CTA and a small "Or tap to speak" link demotes voice
          to a secondary option (per PO decision on LEVI-FIRST-01 v2).
          Both the toggle and the textarea are hidden while the inline
          intake surface is mounted so the contractor has one clear
          input at a time. */}
      {!inIntake && !typeMode ? (
        <button type="button" onClick={() => setTypeMode(true)}
          className="text-xs text-[var(--jc-text-soft)] underline inline-flex items-center gap-1"
          data-testid="levi-cmd-type-toggle">
          <Keyboard size={12}/> {t.type}
        </button>
      ) : (!inIntake && (
        <form onSubmit={(e) => { e.preventDefault(); if (draft.trim()) { cmd.submitText(draft.trim()); setDraft(""); } }} className="space-y-2">
          <textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)}
            ref={textareaRef}
            /* P1-A · PO manual QA — modern chat-app composer:
               Enter sends, Shift+Enter inserts a newline. Only on
               pointer-fine hosts (desktop / trackpad); on touch
               devices Enter must remain a newline so mobile
               multiline input is unaffected. matchMedia guards the
               feature detection so we never change mobile behavior. */
            onKeyDown={(e) => {
              if (e.key !== "Enter" || e.shiftKey || e.nativeEvent?.isComposing) return;
              const hasFinePointer = typeof window !== "undefined"
                && typeof window.matchMedia === "function"
                && window.matchMedia("(pointer: fine)").matches;
              if (!hasFinePointer) return;
              e.preventDefault();
              const value = draft.trim();
              if (value) { cmd.submitText(value); setDraft(""); }
            }}
            className="w-full rounded-md border border-[var(--jc-border)] p-2 text-sm"
            placeholder={lang === "es" ? "Escribe lo que le dirías a Levi…" : "Type what you'd say to Levi…"}
            data-testid="levi-cmd-text-input"/>
          <div className="flex items-center gap-3">
            <button type="submit" className="rounded bg-black text-white text-xs px-3 py-1.5 font-semibold"
              data-testid="levi-cmd-text-submit">
              {lang === "es" ? "Enviar" : "Send"}
            </button>
            <button type="button"
              onClick={() => { setTypeMode(false); cmd.startListening?.(); }}
              className="text-xs text-[var(--jc-text-soft)] underline inline-flex items-center gap-1"
              data-testid="levi-cmd-switch-to-voice">
              <Mic size={12}/> {lang === "es" ? "O toca para hablar" : "Or tap to speak"}
            </button>
            <button type="button"
              className="text-xs text-[var(--jc-text-soft)] underline ml-auto"
              onClick={() => setTypeMode(false)}>
              {lang === "es" ? "Cerrar" : "Close"}
            </button>
          </div>
        </form>
      ))}

      {cmd.error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm p-2 inline-flex items-start gap-2" data-testid="levi-cmd-error">
          <AlertTriangle size={14} className="mt-0.5"/> <div>{cmd.error}</div>
        </div>
      )}
    </div>
  );
}
